/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
    Box,
    Collapse,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Skeleton,
    Tooltip,
    useBoolean
} from '@chakra-ui/react'
import { Icon } from 'components/atoms/Icon'
import {
    ForwardRefRenderFunction,
    forwardRef,
    useEffect,
    useState
} from 'react'
import { FieldError } from 'react-hook-form'
import formatter from 'utils/formatter'
import { validatorsForm } from '../../utils/validatorsForm'
import { InputBuildProps } from './interfaces'
import { FormControlCustom } from './styles'

const InputBuildFC: ForwardRefRenderFunction<
    HTMLInputElement,
    InputBuildProps
> = (
    {
        id,
        label,
        placeholder,
        maskReplace,
        err,
        iconName,
        isLoading,
        isSearching,
        isDisable,
        isTooltip,
        size,
        iconClick,
        ...props
    },
    ref
) => {
    const [valueCurrent, setValueCurrent] = useState(
        props.defaultValue || props.value || ''
    )

    const named = id.split('.').slice(-1)[0] || ''

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        handleValue(value)
    }

    const handleValue = (str: string) => {
        if (maskReplace && str) {
            setValueCurrent(formatter[maskReplace](str))
        } else {
            setValueCurrent(str)
        }
    }

    const handleErrorMessage = (err: FieldError): string => {
        const erro = validatorsForm[named]
            ? validatorsForm[named][err.type] ?? (err.message as string)
            : (err.message as string)
        return erro
    }

    const [passwordView, setPasswordView] = useBoolean(false)

    useEffect(() => {
        handleValue(props.value as string)
    }, [props.value])

    return (
        <>
            {isLoading ? (
                <>
                    <Skeleton
                        height="24px"
                        width="50%"
                        borderRadius="md"
                        marginBottom={2}
                    />
                    <Skeleton height="40px" width="full" borderRadius="md">
                        <input ref={ref} hidden />
                    </Skeleton>
                </>
            ) : (
                <FormControlCustom
                    isInvalid={!!err}
                    isRequired={props?.required}
                >
                    <Box display="flex" alignItems="center">
                        <FormLabel
                            textAlign="inherit"
                            mx={0}
                            htmlFor={id}
                            display="flex"
                            alignItems="center"
                        >
                            {label}
                        </FormLabel>
                        {isTooltip && (
                            <Tooltip
                                hasArrow
                                label={isTooltip}
                                textAlign="center"
                                placement="top"
                            >
                                <IconButton
                                    paddingBottom="0.5rem"
                                    colorScheme="transparent"
                                    color="black"
                                    aria-label=""
                                    icon={<Icon name="question-mark" />}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    {isSearching ? (
                        <Skeleton height="40px" width="full" borderRadius="md">
                            <input ref={ref} hidden />
                        </Skeleton>
                    ) : (
                        <InputGroup>
                            <Input
                                data-testid={id}
                                placeholder={placeholder || 'Digite aqui'}
                                {...props}
                                type={
                                    props.type === 'password'
                                        ? passwordView
                                            ? 'text'
                                            : 'password'
                                        : props.type
                                }
                                value={valueCurrent ?? ''}
                                onChangeCapture={handleChange}
                                ref={ref}
                                required={false}
                                disabled={isDisable}
                                max={
                                    props.max
                                        ? props.max
                                        : props.type === 'date'
                                        ? '9999-12-31'
                                        : props.type !== undefined
                                        ? props.max
                                        : ''
                                }
                            />
                            {props.type === 'password' ? (
                                <InputRightElement
                                    cursor="pointer"
                                    children={
                                        <Icon
                                            data-testid={`input-icon-${id}`}
                                            name={
                                                passwordView
                                                    ? 'eye-slash'
                                                    : 'eye'
                                            }
                                            onClick={setPasswordView.toggle}
                                        />
                                    }
                                />
                            ) : (
                                iconName && (
                                    <InputRightElement
                                        cursor="pointer"
                                        children={
                                            <Icon
                                                data-testid={`input-icon-${id}`}
                                                name={iconName}
                                                onClick={iconClick}
                                            />
                                        }
                                    />
                                )
                            )}
                        </InputGroup>
                    )}
                    <Collapse in={!!err} animateOpacity>
                        <Box minH="15px">
                            <FormErrorMessage
                                position="relative"
                                mt={1}
                                fontSize="xs"
                            >
                                {err && handleErrorMessage(err)}
                            </FormErrorMessage>
                        </Box>
                    </Collapse>
                </FormControlCustom>
            )}
        </>
    )
}

export const InputBuild = forwardRef(InputBuildFC)
